import type React from "react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";

import type { UserDetailsDto, UserListItemDto } from "@/api/types";
import { ClockPlusIcon } from "@/components/Icons/ClockPlusIcon";
import type { ModalBaseProps } from "@/components/Modal/Modal";
import { Modal } from "@/components/Modal/Modal";
import { Box } from "@/design-system/Box";
import { Button } from "@/design-system/Button";
import { Checkbox } from "@/design-system/Checkbox";
import { Notice } from "@/design-system/Notice";
import { Stack } from "@/design-system/Stack";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useBool } from "@/hooks/useBool";
import { useSlug } from "@/hooks/useSlug";
import { userMutations } from "@/queries/users/mutations";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

import { ScheduleUserDeletionModal } from "./ScheduleUserDeletionModal";

type DeleteResidentModalProps = ModalBaseProps & {
  user: UserDetailsDto | UserListItemDto;
};

export function DeleteResidentModal({
  user,
  isOpened,
  onOpenChange,
  onExitComplete,
}: DeleteResidentModalProps): React.ReactNode {
  const [isSetDeletionDateModalOpened, setDeletionModalOpenHandlers] = useBool(false);
  const [isConfirmed, setIsConfirmed] = useState(false);

  const sessionUser = useSessionUser();
  const slug = useSlug();
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    // oxlint-disable-next-line react-hooks/set-state-in-effect
    setIsConfirmed(false);
  }, [isOpened]);

  const deleteUserMutation = userMutations.useDeleteUser();

  const handleSetDeletionDate = () => {
    setDeletionModalOpenHandlers.set(true);
  };

  const handleAfterSetDeletionDate = () => {
    onOpenChange(false);
  };

  const handleConfirmDeletion = () => {
    if (!user) {
      return;
    }

    deleteUserMutation.mutate(
      { userId: user.id, projectIds: [sessionUser.project.id] },
      {
        onSuccess: () => {
          if (!sessionUser.isSuperAdmin && sessionUser.isAdmin) {
            void navigate(routes.users.list({ slug }));
          }

          onOpenChange(false);
        },
      },
    );
  };
  const isDeletionScheduled = Boolean(user.deleteAfter);
  const isUserOnboarded = Boolean(user.registeredAt);

  return (
    <>
      <Modal.Root
        title={t("page.user-detail.delete-or-date-modal.title")}
        data-testid="delete-resident-modal"
        shouldCloseOnClickOutside={false}
        shouldCloseOnEsc={false}
        onExitComplete={onExitComplete}
        {...{ isOpened, onOpenChange }}
      >
        <Modal.Body>
          <Stack gap="md">
            <p className="text-text-secondary">
              {isDeletionScheduled
                ? t("page.user-detail.delete-or-date-modal.description-already-set")
                : t("page.user-detail.delete-or-date-modal.description")}
            </p>
            <Checkbox
              data-testid="confirm-delete-checkbox"
              id="checkbox-confirm"
              name="isConfirmed"
              checked={isConfirmed}
              onChange={(e) => setIsConfirmed(e.target.checked)}
              label={t("page.user-detail.delete-or-date-modal.confirm-checkbox")}
            />

            {isConfirmed && isUserOnboarded && (
              <Box padding={{ top: "md" }}>
                <Notice variant="special" message={t("page.user-detail.delete-or-date-modal.notice.notify-by-email")} />
              </Box>
            )}
          </Stack>
        </Modal.Body>
        <Modal.Actions>
          <Button
            onClick={handleSetDeletionDate}
            data-testid="delete-user-modal-set-deletion-btn"
            variant="outline"
            icon={<ClockPlusIcon />}
            disabled={!isConfirmed && !isDeletionScheduled}
          >
            {isDeletionScheduled
              ? t("page.user-detail.delete-or-date-modal.action.update-deletion-date")
              : t("page.user-detail.delete-or-date-modal.action.set-deletion-date")}
          </Button>
          <Button
            onClick={handleConfirmDeletion}
            variant="filled"
            tone="error"
            data-testid="confirm-delete-btn"
            isLoading={deleteUserMutation.isPending}
            disabled={!isConfirmed}
          >
            {t("page.user-detail.delete-or-date-modal.action.delete")}
          </Button>
        </Modal.Actions>
      </Modal.Root>
      <ScheduleUserDeletionModal
        user={user}
        projectIds={[sessionUser.project.id]}
        isOpened={isSetDeletionDateModalOpened}
        onOpenChange={setDeletionModalOpenHandlers.set}
        onSetDeletionDate={handleAfterSetDeletionDate}
      />
    </>
  );
}
